import React, { useState, useEffect } from 'react';
import Spinner from './Spinner';
import { useConfig } from "../ConfigContext";

const BookingCalendar = ({onChange, value, locationType, service, startDate, error}) => {
  const { config, error: configError } = useConfig();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(value);
  const [appointments, setAppointments] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [busy, setBusy] = useState(false);

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const startingDay = firstDayOfMonth.getDay();

  const prevMonth = (e) => {
    e.preventDefault();
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    setSelectedDate(null);
    handleTimeSlotClick(null);
  };

  const nextMonth = (e) => {
    e.preventDefault();
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    setSelectedDate(null);
    handleTimeSlotClick(null);
  };

  const handleDayClick = (day) => {
    setSelectedDate(day);
    handleTimeSlotClick(null);
  };


  const handleTimeSlotClick = (timeSlot) => {
    setSelectedTimeSlot(timeSlot?.id);
    onChange(timeSlot?.id);
  };


  const renderDays = () => {
    const days = [];
  
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="empty-day"></div>);
    }

    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Get the full date of the current date
    const maxSelectableDate = new Date(todayDate);
    const bookingDays = config['Bookable Days']?.apt_numbervalue ?? 30;
    maxSelectableDate.setDate(maxSelectableDate.getDate() + bookingDays);
  
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isBooked = appointments.some(appointment => appointment.getTime() === date.getTime());
      const hasAvailableSlots = availableTimeSlots[date.getTime()] && availableTimeSlots[date.getTime()].length > 0;
      const isFutureDay = date > maxSelectableDate; // Check if the day is beyond 1 month into the future
      const isPastDay = date < todayDate; // Check if the day is in the past
      const isCurrentDay = !(date < todayDate) && !(date > todayDate);
  
      days.push(
        <div
          key={`day-${day}`}
          className={`day ${isBooked ? 'booked' : ''} ${selectedDate && selectedDate.getTime() === date.getTime() ? 'selected' : ''} ${hasAvailableSlots ? 'available' : ''} ${isCurrentDay ? 'current-day' : ''} ${isPastDay ? 'past-day' : ''} ${isFutureDay ? 'future-day' : ''}`}
          onClick={() => !isPastDay && !isFutureDay && handleDayClick(date)}
        >
          {day}
          {!isPastDay && !isFutureDay && hasAvailableSlots && <div className="dot"></div>}
        </div>
      );
    }
  
    return days;
  };

  useEffect(() => {
    // only set busy if fetch takes longer than 500ms so we don't have flashing on the screen
    const timeoutId = setTimeout(() => {
      setBusy(true);
    }, 500);
    // get available time slots for next month
    fetch(`${config.apiUrl}/appointment-slots?locationType=${locationType}&service=${service}&startDate=${startDate.toISOString()}`, 
    { headers: {
      "Api-Key": config.apiKey,
      // "Ocp-Apim-Trace": "true"
    }})
    .then(response => { 
      if(response.status === 200) return response.json();
      if(response.status === 204) return [];
    })
    .then(data => {
      // Assuming the API response is an array of objects with 'date' and 'timeslots' properties
      const allAvailableSlots = {};
      data.forEach(item => {
        const date = new Date(item.app_start);
        let availableSlots = allAvailableSlots[date.setHours(0,0,0,0)] || [];
        availableSlots.push({id: item.app_appointmentslotid, start: new Date(item.app_start), end: new Date(item.app_end)});
        allAvailableSlots[date.setHours(0,0,0,0)] = availableSlots
      });
      setAvailableTimeSlots(allAvailableSlots);
    })
    .catch(error => {
      console.error('Error fetching timeslot data:', error);
    }).finally(() => {
      clearTimeout(timeoutId);
      // setSelectedDate(null);
      setBusy(false);
    });
  }, [locationType, service, error]);

  return (
    <div className="booking-calendar">
      <div className="calendar-header">
        <button onClick={prevMonth}>&lt;</button>
        <h3>{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
        <button onClick={nextMonth}>&gt;</button>
      </div>
      <div className="days-of-week">
        {daysOfWeek.map(day => (
          <div key={day} className="day-of-week">{day}</div>
        ))}
      </div>
      { busy ? <div className="spinner-container"><Spinner/></div> : Object.keys(availableTimeSlots).length === 0 ? <div>There are currently no available time slots</div> : <div className="days">{ renderDays() }</div>}
      <div className="selected-date">
  {selectedDate && (
    <div className="time-slots">
      <p>Selected Date: {selectedDate.toDateString()}</p>
      <ul className="time-slots">
        {selectedDate && availableTimeSlots[selectedDate.getTime()] ? (
          availableTimeSlots[selectedDate.getTime()].map((timeSlot, index) => (
            <li
              key={index}
              className={selectedTimeSlot === timeSlot.id ? 'selected' : ''}
              onClick={() => handleTimeSlotClick(timeSlot)}
            >
              {config.timeFormatter.format(timeSlot.start)} - {config.timeFormatter.format(timeSlot.end)}
            </li>
          ))
        ) : (
          <li>No available time slots for this day.</li>
        )}
      </ul>
    </div>
  )}
</div>
  {error && <div className="error"> {error.message}</div>}
    {Object.keys(availableTimeSlots).length === 0 && <div>Appointments are subject to availability, please send a written review request if no appointment time slots are available.</div>}
    </div>
  );
};

export default BookingCalendar;
